// SPDX-License-Identifier: Elastic-2.0
/**
 * networkMock — MSW handlers + fixtures for A2 Network step.
 *
 * Test-only (T-007): the A2 step now talks to the real generated gateway
 * client (`NetworkService`, see `A2Network.tsx`). These handlers exist
 * purely so the component's behaviour can be exercised in isolation —
 * they are consumed test-side via `msw/node` + `setupServer` (see
 * `A2Network.test.tsx`). NO global service worker is registered in dev.
 *
 * The generated client issues requests against `OpenAPI.BASE`, which is
 * the empty string under test (`initGatewayClient()` is not called), so
 * the effective paths are the bare `/network/*` routes below — NOT the
 * `/api/mirlai/v1/...` prefix (that prefix is the gateway mount point,
 * applied at runtime via the configured base URL).
 *
 * The wire shapes here intentionally mirror the generated OpenAPI models
 * (`WifiNetwork`, `EthernetStatus`, `WifiConnectResponse`, …). They are a
 * local test convenience only — the app imports its types from
 * `@/api/gateway`.
 */
import { HttpResponse, delay, http } from "msw";

// ── Wire types (mirror the generated F-300 OpenAPI shapes) ──────────────

/** Single discovered WiFi access point. */
export interface WifiNetwork {
  ssid: string;
  /** Signal strength 1-4 (4 = best). */
  signal: 1 | 2 | 3 | 4;
  /** Whether the network requires a passphrase. */
  secure: boolean;
  /** Optional: previously joined from this device. */
  saved?: boolean;
}

/** Result of `GET /network/status`. */
export type NetworkStatus =
  | {
      kind: "ethernet";
      online: boolean;
      /** Negotiated link speed in Mbps. */
      speed_mbps: number;
    }
  | {
      kind: "wifi";
      online: boolean;
      ssid?: string;
    }
  | {
      kind: "none";
      online: false;
    };

/** Request body for `POST /network/wifi/connect`. */
export interface WifiConnectRequest {
  ssid: string;
  password: string;
}

/** Response body for `POST /network/wifi/connect`. */
export interface WifiConnectResponse {
  ok: boolean;
  ssid: string;
}

// ── Fixtures ────────────────────────────────────────────────────────────

/**
 * The 6-network fixture mandated by the T-015 spec. Order matches the
 * design-system reference (`A2Network.jsx` lines 3-10) so the visual
 * regression check against the JSX kit holds.
 */
export const FIXTURE_NETWORKS: readonly WifiNetwork[] = [
  { ssid: "Casa Palermo", signal: 4, secure: true, saved: true },
  { ssid: "Casa Palermo 5G", signal: 4, secure: true },
  { ssid: "Vecino-2.4", signal: 2, secure: true },
  { ssid: "Pereyra-WiFi", signal: 3, secure: true },
  { ssid: "Movistar_AC18", signal: 2, secure: true },
  { ssid: "Personal_Flow", signal: 1, secure: true },
];

/** Default status fixture: ethernet up at 1 Gbps. */
export const FIXTURE_STATUS_ETHERNET: NetworkStatus = {
  kind: "ethernet",
  online: true,
  speed_mbps: 1000,
};

/**
 * Base path the generated client hits under test. `OpenAPI.BASE` is `""`
 * in the test environment (no `initGatewayClient()`), so `NetworkService`
 * requests resolve to `/network/...` — these handlers match exactly that.
 */
export const NETWORK_API_BASE = "/network";

// ── MSW handlers ────────────────────────────────────────────────────────

/** Latency for the POST /connect mock, in milliseconds. */
const CONNECT_LATENCY_MS = 1500;

/**
 * Default handler set. Tests can compose or override by appending their
 * own handlers to the returned array (`server.use(...customHandlers)`).
 */
export const networkHandlers = [
  http.get(`${NETWORK_API_BASE}/status`, () => {
    return HttpResponse.json(FIXTURE_STATUS_ETHERNET);
  }),

  http.get(`${NETWORK_API_BASE}/wifi/scan`, () => {
    return HttpResponse.json({ networks: FIXTURE_NETWORKS });
  }),

  http.post(`${NETWORK_API_BASE}/wifi/connect`, async ({ request }) => {
    const body = (await request.json()) as WifiConnectRequest;
    await delay(CONNECT_LATENCY_MS);
    return HttpResponse.json<WifiConnectResponse>({
      ok: true,
      ssid: body.ssid,
    });
  }),
];

/**
 * Connect-failure handler variant (T-007). Returns a 200 envelope with
 * `ok: false` — the "credentials understood but the device did not come
 * online" case (wrong password / weak signal / timeout; functional § 3.3).
 * Override the default connect handler with this in tests via
 * `server.use(connectFailureHandler)`.
 */
export const connectFailureHandler = http.post(
  `${NETWORK_API_BASE}/wifi/connect`,
  async ({ request }) => {
    const body = (await request.json()) as WifiConnectRequest;
    return HttpResponse.json<WifiConnectResponse>({
      ok: false,
      ssid: body.ssid,
    });
  },
);
